import React, { useState, useEffect } from 'react';
import { X, Download, Copy, FileText } from 'lucide-react';
// Se você tem date-fns instalado, pode usar:
// import { format, parseISO } from 'date-fns';

const ExportCouponsModal = ({ 
  isOpen, 
  onClose, 
  data = [], 
  availableFields = [], 
  defaultSelectedFields = [],
  onExport 
}) => {
  const [selectedFields, setSelectedFields] = useState([]);
  const [includeTrash, setIncludeTrash] = useState(false);
  const [exportedData, setExportedData] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  // Inicializa campos selecionados com os padrões
  useEffect(() => {
    if (defaultSelectedFields.length > 0) {
      setSelectedFields(defaultSelectedFields);
    }
  }, [defaultSelectedFields]);

  // Fecha modal ao pressionar ESC
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleFieldChange = (fieldName) => {
    setSelectedFields(prev => {
      if (prev.includes(fieldName)) {
        return prev.filter(field => field !== fieldName);
      } else {
        return [...prev, fieldName];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedFields.length === availableFields.length) {
      setSelectedFields([]);
    } else {
      setSelectedFields(availableFields.map(field => field.name));
    }
  };

  const generateTextData = () => {
    setIsGenerating(true);
    
    try {
      // Filtra dados baseado na opção de lixeira
      const filteredData = includeTrash ? data : data.filter(item => !item.is_deleted);
      
      // Cria o cabeçalho baseado nos campos selecionados
      const selectedFieldObjects = availableFields.filter(field => 
        selectedFields.includes(field.name)
      );
      
      // Cria as linhas de dados (sem cabeçalho)
      const rows = filteredData.map(row => 
        selectedFieldObjects.map(field => {
          const value = row[field.name];
          
          // Tratamento especial para diferentes tipos de campos
          if (value === null || value === undefined) {
            return '';
          }
          
          // Formatação para campo booleano is_on_tr
          if (field.name === 'is_on_tr') {
            return value === true ? 'Sim' : 'Não';
          }
          
          // Formatação para datas (se contém 'time' no nome do campo)
          if (field.name.includes('time') && value) {
            try {
              const date = new Date(value);
              return date.toLocaleString('pt-BR');
            } catch (e) {
              return value.toString();
            }
          }
          
          return value.toString();
        }).join('\t')
      );
      
      // Junta apenas as linhas de dados (sem cabeçalho)
      const textContent = rows.join('\n');
      setExportedData(textContent);
      
    } catch (error) {
      console.error('Erro ao gerar dados:', error);
      setExportedData('Erro ao gerar dados de exportação');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(exportedData);
      // Você pode adicionar um toast/snackbar aqui se quiser
      console.log('Dados copiados para a área de transferência');
    } catch (err) {
      console.error('Erro ao copiar:', err);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([exportedData], { type: 'text/plain;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'cupons_exportados.txt');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportAndClose = () => {
    if (onExport && exportedData) {
      onExport(exportedData, selectedFields);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="h-5 w-5 text-orange-500" />
            Exportar Cupons
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 p-6 overflow-auto">
          <div className="space-y-6">
            {/* Seleção de campos */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Selecione os campos para exportação
              </label>
              
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                {/* Lista de campos */}
                <div className="lg:col-span-2">
                  <div className="border rounded-lg p-3 max-h-48 overflow-y-auto">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-gray-600">
                        {selectedFields.length} de {availableFields.length} selecionados
                      </span>
                      <button
                        onClick={handleSelectAll}
                        className="text-sm text-orange-600 hover:text-orange-800"
                      >
                        {selectedFields.length === availableFields.length ? 'Desmarcar todos' : 'Selecionar todos'}
                      </button>
                    </div>
                    
                    <div className="space-y-2">
                      {availableFields.map((field) => (
                        <label key={field.name} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedFields.includes(field.name)}
                            onChange={() => handleFieldChange(field.name)}
                            className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                          />
                          <span className="text-sm text-gray-700">{field.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Opções adicionais */}
                <div className="space-y-4">
                  <div>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeTrash}
                        onChange={(e) => setIncludeTrash(e.target.checked)}
                        className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                      />
                      <span className="text-sm text-gray-700">Incluir lixeira</span>
                    </label>
                  </div>
                </div>

                {/* Botão Gerar */}
                <div className="flex items-end">
                  <button
                    onClick={generateTextData}
                    disabled={selectedFields.length === 0 || isGenerating}
                    className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
                  >
                    <Download className="h-4 w-4" />
                    {isGenerating ? 'Gerando...' : 'Gerar'}
                  </button>
                </div>
              </div>
            </div>

            {/* Área de preview dos dados */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preview dos dados exportados
              </label>
              <textarea
                value={exportedData}
                readOnly
                rows={12}
                className="w-full border border-gray-300 rounded-lg p-3 font-mono text-sm bg-gray-50 resize-none"
                placeholder="Os dados exportados aparecerão aqui após clicar em 'Gerar'..."
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <button
            onClick={handleCopyToClipboard}
            disabled={!exportedData}
            className="bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Copy className="h-4 w-4" />
            Copiar
          </button>
          
          <div className="flex gap-3">
            <button
              onClick={handleDownload}
              disabled={!exportedData}
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Download className="h-4 w-4" />
              Download
            </button>
            
            <button
              onClick={onClose}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportCouponsModal;